import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { CommonService } from 'src/app/services/common.service';
import {
  GroupOutput,
  LockdownConfigurations,
  LockdownGroupInfo,
  LockdownGroupsResults,
  LockdownsGroupSummary,
} from '../../models/lockdown/lockdown';
import { LockdownService } from '../../services/lockdown.service';
import { AddToastMessage } from '../shared/shared.actions';
import { SharedState } from '../shared/shared.state';
import {
  GetConfigurations,
  LoadLockdownGroups,
  LoadMoreResults,
  LockdownGroupOutputs,
  SelectGroup,
  UpdateLockdowns,
  UpdateLockdownsByAccessPointStatus,
  UpdateSelectedLockdown,
  UpdateSerchTerm,
} from './lockdown.actions';
import { LockdownStateModel } from './lockdown.model';
import {MonitoringService} from '../../../services/logging.service';
import { TranslateService } from '@ngx-translate/core';
import { first } from 'rxjs/operators';

const lockdownCommandsMapper: any = {
  1: 'Locking',
  2: 'Unlocking',
  3: 'Open',
};

@State<LockdownStateModel>({
  name: 'lockdown',
  defaults: {
    groups: null,
    selectedGroupInfo: null,
    selectedGroupOutputs: null,
    lockdownGroupgroupListSearchTerm: '',
    currentGroupsPageNumber: 0,
    configurations: null,
    totalGroupsCount: 0,
    currentPage: 0,
    searchTerm: '',
    isTouched: false,
    isLoadingData: false,
    selectedGroupInfoUpdated: false,
  },
})
@Injectable()
export class LockdownState {
  constructor(
    private readonly lockdownService: LockdownService,
    private store: Store,
    private commonService: CommonService,
    private monitoringService: MonitoringService,
    private translate: TranslateService
  ) {}

  @Selector([LockdownState])
  static configurations(state: LockdownStateModel): LockdownConfigurations {
    return state.configurations;
  }

  @Selector([LockdownState])
  static lockdowns(state: LockdownStateModel): LockdownsGroupSummary[] {
    return state.groups;
  }

  @Selector([LockdownState])
  static selectedLockdownGroup(state: LockdownStateModel): LockdownGroupInfo {
    return state.selectedGroupInfo;
  }

  @Selector([LockdownState])
  static selectedLockdownGroupOutputs(
    state: LockdownStateModel
  ): GroupOutput[] {
    return state.selectedGroupOutputs;
  }

  @Action(LockdownGroupOutputs)
  async LockdownGroupOutputs(
    ctx: StateContext<LockdownStateModel>,
    action: LockdownGroupOutputs
  ) {
    this.lockdownService
      .sendLockdownOutputsCommand(action.lockForm.command)
      .then(() => {
        const {commandTypeId, lockdownOutputs} = action.lockForm.command;
        let title: string;
        let message: string;
        let command: string;
        switch (lockdownCommandsMapper[commandTypeId]) {
          case 'Locking':
            this.translate.get(
              'lockdown.side.command.locking').pipe(first()).subscribe(res => {
                command = res;
            });
            break;
          case 'Unlocking':
            this.translate.get(
              'lockdown.side.command.unlocking').pipe(first()).subscribe(res => {
                command = res;
            });
            break;
          case 'Open':
            this.translate.get(
              'lockdown.side.command.open').pipe(first()).subscribe(res => {
                command = res;
            });
            break;
          default:
            break;
        }
        this.translate.get([
          'lockdown.side.lockdownComplete.title',
          'lockdown.side.lockdownComplete.message',
        ], {
          command,
          number: !!lockdownOutputs.length ? lockdownOutputs.length : 1
        }).pipe(first()).subscribe(res => {
          title = res['lockdown.side.lockdownComplete.title'];
          message = res['lockdown.side.lockdownComplete.message'];
        });
        this.store.dispatch(
          new AddToastMessage({
            severity: 'success',
            summary: title,
            detail: message,
          })
        );
      })
      .then(() => {
        this.store.dispatch(new LoadLockdownGroups());
      })
      .catch((error) => {this.monitoringService.logException(error);}
      );
  }

  @Action(SelectGroup)
  async selectGroup(
    ctx: StateContext<LockdownStateModel>,
    action: SelectGroup
  ) {
    if (action.groupId) {
      try {
        let response = await this.lockdownService.getGroupDetailsById(
          action.groupId
        );
        if(response){
          if (response?.lockdown?.outputs === null) {
            response.lockdown.outputs = [];
          }
          ctx.patchState({
            selectedGroupInfo: response.lockdown,
            selectedGroupOutputs: response.lockdown.outputs,
          });
        }
      } catch (error) {
        this.monitoringService.logException(error);
        // this.store.dispatch(
        //   new AddToastMessage({
        //     severity: 'error',
        //     summary: 'loading group failed',
        //     detail: error,
        //   })
        // );
      }
    } else {
      ctx.patchState({ selectedGroupInfo: null, selectedGroupOutputs: null });
    }
  }

  @Action(GetConfigurations)
  async getConfiguratiosn(ctx: StateContext<LockdownStateModel>) {
    const res = await this.lockdownService.getLockdownConfigurations();
    if(res){
      ctx.patchState({ configurations: res });
    }
  }

  @Action(LoadLockdownGroups)
  async loadGroups(
    ctx: StateContext<LockdownStateModel>,
    action: LoadLockdownGroups
  ) {
    ctx.patchState({ isLoadingData: true });
    let res = await this.lockdownService.getGroupsSummary(
      1,
      0,
      ctx.getState().searchTerm
    );
    if(res){
      if (res?.lockdowns && res.lockdowns?.results) {
        res.lockdowns.results.sort(
          this.commonService.compareByNameAscFn
        );
      }
      ctx.patchState({
        groups: res?.lockdowns?.results,
        totalGroupsCount: res?.lockdowns?.totalCount,
        isTouched: true,
        isLoadingData: false,
      });
    }
  }

  @Action(LoadMoreResults)
  async getMoreGroups(
    ctx: StateContext<LockdownStateModel>,
    action: LoadMoreResults
  ) {
    ctx.patchState({isLoadingData: true});
    if (action.resetChunk) {
      ctx.patchState({currentPage: 0});
    }
    let {currentPage, groups} = ctx.getState();
    const chunkSize = this.store.selectSnapshot(SharedState.resultsChunkSize);
    let nextPage: number = currentPage;
    let groupsResult: LockdownGroupsResults = await this.lockdownService.getGroupsSummary(
      ++nextPage,
      chunkSize,
      ctx.getState().searchTerm
    );

    if (groupsResult) {
      const groupList = action.resetChunk ? [] : groups;
      if (groupsResult.lockdowns && groupsResult.lockdowns.results) {
        groupsResult.lockdowns.results.sort(
          this.commonService.compareByNameAscFn
        );
      }
      ctx.patchState({
        groups: [...groupList, ...groupsResult.lockdowns.results],
        totalGroupsCount: groupsResult.lockdowns.totalCount,
        isTouched: true,
        isLoadingData: false,
      });
      if (groupList.length !== ctx.getState().groups.length) {
        ctx.patchState({currentPage: nextPage});
      }
    }
  }

  @Action(UpdateSerchTerm)
  async updateSerchTerm(
    ctx: StateContext<LockdownStateModel>,
    action: UpdateSerchTerm
  ) {
    ctx.patchState({ searchTerm: action.searchTerm });
  }

  @Action(UpdateLockdowns)
  async updateLockdowns(
    ctx: StateContext<LockdownStateModel>,
    action: UpdateLockdowns
  ) {
    if (action?.lockdownUpdate) {
      const { groups } = ctx.getState();
      if (groups) {
        const existedLockdownItem = groups.find(
          (group) => group.id === action.lockdownUpdate.Id
        );
        if (existedLockdownItem) {
          const selectedIndex = groups.indexOf(existedLockdownItem);
          groups[selectedIndex] = {
            ...groups[selectedIndex],
            name: action.lockdownUpdate.Name,
            description: action.lockdownUpdate.Description,
            groupRemainingTimeSeconds:action.lockdownUpdate.GroupRemainingTimeSeconds,
            unlockCardsCount: action.lockdownUpdate.UnlockCardsCount,
            lockCardsCount: action.lockdownUpdate.LockCardsCount,
            doorsCount: action.lockdownUpdate.DoorsCount,
            outputsCount: action.lockdownUpdate.OutputsCount,
            immunedEmployeesCount: action.lockdownUpdate.ImmunedEmployeesCount,
            status: action.lockdownUpdate.Status
          };

          groups[selectedIndex]['totalLockdown'] = action?.lockdownUpdate?.['TotalLockdown'];

          ctx.patchState({
            groups: [...groups],
          });
        }
      }
    }
  }

  @Action(UpdateSelectedLockdown)
  async updateSelectedLockdown(
    ctx: StateContext<LockdownStateModel>,
    action: UpdateSelectedLockdown
  ) {
    if (action?.selectedLockdownUpdate) {
      let { groups, selectedGroupInfo, selectedGroupOutputs } = ctx.getState();
      if (groups) {
        if (action.selectedLockdownUpdate?.LockdownOutputs === null) {
          action.selectedLockdownUpdate.LockdownOutputs = [];
        }
        if (selectedGroupInfo.id === action.selectedLockdownUpdate.Id) {
          let newSelectedGroupInfo = {
            ...selectedGroupInfo,
            name: action.selectedLockdownUpdate.Name,
            description: action.selectedLockdownUpdate.Description,
            groupRemainingTimeSeconds:
              action.selectedLockdownUpdate.GroupRemainingTimeSeconds,
            status: action.selectedLockdownUpdate.Status,
          };

          if (action.selectedLockdownUpdate.LockdownOutputs) {
            for (let i = 0; i < selectedGroupOutputs.length; i++) {
              let output = selectedGroupOutputs[i];
              let existedOutput = action.selectedLockdownUpdate.LockdownOutputs.find(
                (actionOutput) =>
                  actionOutput.PanelId === output.panelId &&
                  actionOutput.OutputSlot === output.outputSlot
              );
              selectedGroupOutputs[i] = {
                ...output,
                outputRemainingTimeSeconds:
                  existedOutput.OutputRemainingTimeSeconds,
                name: existedOutput.Name,
                description: existedOutput.Description,
                status: existedOutput.Status,
                lockownOutputTypeName: existedOutput.LockownOutputTypeName,
                lockownOutputTypeId: existedOutput.LockownOutputTypeId,
              };
            }
          }

          ctx.patchState({
            selectedGroupInfo: newSelectedGroupInfo,
            selectedGroupOutputs,
            selectedGroupInfoUpdated: true,
          });
        }
      }
    }
  }

  @Action(UpdateLockdownsByAccessPointStatus)
  async updateLockdownsByAccessPointStatus(
    ctx: StateContext<LockdownStateModel>,
    action: UpdateLockdownsByAccessPointStatus
  ) {
    if (action?.accessPointStatusUpdate) {
      let {
        selectedGroupInfo,
        selectedGroupOutputs,
      } = ctx.getState();
      if (selectedGroupOutputs &&
        action?.accessPointStatusUpdate.accessPoints &&
        action?.accessPointStatusUpdate.accessPoints.length > 0) {
        let changedAccessPoint =
          action?.accessPointStatusUpdate.accessPoints[0];
        let splitedId = changedAccessPoint.id
          ? changedAccessPoint.id.split('_')
          : [];
        let panelId =
          splitedId && splitedId.length > 0
            ? Number.parseInt(splitedId[0], 10)
            : 0;
        let outputSlot =
          splitedId && splitedId.length > 0
            ? Number.parseInt(splitedId[1], 10)
            : 0;

        for (const output of selectedGroupOutputs) {
          if (
            output.panelId === panelId &&
            output.outputSlot === outputSlot
          ) {
            output.isLockdown = changedAccessPoint.isLockdown;
            if (
              output.outputRemainingTimeSeconds > 0 &&
              changedAccessPoint.isLockdown
            ) {
              output.outputRemainingTimeSeconds = --output.outputRemainingTimeSeconds;
            } else if (!changedAccessPoint.isLockdown && output.status === 'Lock') {
              await this.reloadSelectedGroupData(ctx, selectedGroupInfo);
              await this.reloadGroupData(ctx);
            }
          }
        }
      }
    }
  }

  private async reloadSelectedGroupData(
    ctx: StateContext<LockdownStateModel>,
    selectedGroupInfo: LockdownGroupInfo
  ) {
    try {
      let response = await this.lockdownService.getGroupDetailsById(
        selectedGroupInfo.id
      );
      if (response?.lockdown?.outputs === null) {
        response.lockdown.outputs = [];
      }
      if(response){
        ctx.patchState({
          selectedGroupInfo: response.lockdown,
          selectedGroupOutputs: response.lockdown.outputs,
        });
      }else {
        throw new Error('Network error has occurred');
      }

    } catch (error) {
      this.monitoringService.logException(error);
    }
  }

  private async reloadGroupData(
    ctx: StateContext<LockdownStateModel>
  ) {
    try {
      let res = await this.lockdownService.getGroupsSummary(
        1,
        0,
        ctx.getState().searchTerm
      );
      if(res){
        if (res.lockdowns && res.lockdowns.results) {
          res.lockdowns.results.sort(
            this.commonService.compareByNameAscFn
          );
        }
        ctx.patchState({
          groups: res.lockdowns.results,
          totalGroupsCount: res.lockdowns.totalCount,
          isTouched: true,
        });
      }
    } catch (error) {
      this.monitoringService.logException(error);
    }
  }
}
